import { useMemo } from 'react';
import { localToUtcTime } from '@ourbranch/date-helpers';
import { getRoofAgeLimit, statesWithUniqueLogic } from '@ourbranch/roof-helpers';
import { awsDateToDateObject } from 'core/helpers/formatters';
import { useStore } from 'core/store';

import * as stateRoofLogic from './state-roof-logic';

import { getForcedExtendedRSCoverage, getForcedScheduleRSCoverage, shouldFieldBeLocked } from './CW-roof-logic';

const useExtendedRoofCoverage = (home, state, fromPolicy, initialValues, homeCoverage) => {
  const { account, offer: offerStore } = useStore();
  if (!home || !homeCoverage) {
    return null;
  }
  const { coverageRSExtended, deductibleWindHail } = homeCoverage;
  const policyStore = account?.policies?.policy;

  const dateToCheckAgainst = fromPolicy
    ? new Date(localToUtcTime(policyStore?.policy.effectiveDate, state))
    : awsDateToDateObject(offerStore?.offer?.quote?.global.rateControlDate);

  const roofAge = dateToCheckAgainst.getFullYear() - home.roofYear;

  const roofAgeLimit = getRoofAgeLimit({
    roofType: home.roofType,
    state,
    deductibleWindHail: homeCoverage?.deductibleWindHail || 0,
    roofAge
  });

  // get state overrides
  const {
    coverageRSExtended: stateSpecificRoofSurfacesExtended,
    stateSpecificLockField,
    stateOverride
  } = useMemo(() => {
    if (statesWithUniqueLogic.includes(state)) {
      const stateLogic = stateRoofLogic[`${state.toLowerCase()}RoofSurfacesExtendedHelper`];
      return stateLogic(roofAge, deductibleWindHail, coverageRSExtended);
    }
    return {};
  }, [roofAge, deductibleWindHail, coverageRSExtended]);

  const forcedScheduledRSCoverage = getForcedScheduleRSCoverage(
    state,
    roofAge,
    roofAgeLimit,
    stateOverride,
    stateSpecificRoofSurfacesExtended
  );

  const forcedExtendedRSCoverage = getForcedExtendedRSCoverage(
    state,
    roofAge,
    stateOverride,
    stateSpecificRoofSurfacesExtended
  );

  // dont lock the roof choice in policy if there is no change in value
  const inPolicyAndRoofYearUnchanged = fromPolicy && home.roofYear === initialValues.home.roofYear;

  // honor policies already bound this way
  const policyBoundWithRoofExtendedSoWeHaveToHonorItIfRoofYearUnchanged =
    inPolicyAndRoofYearUnchanged && initialValues.homeCoverage.coverageRSExtended;

  // check if the value is what we expect in policy
  const isCurrentValueCorrectValueInPolicy =
    fromPolicy &&
    ((forcedScheduledRSCoverage && !coverageRSExtended) || (forcedExtendedRSCoverage && coverageRSExtended));

  const lockedRSField = shouldFieldBeLocked(
    fromPolicy,
    state,
    isCurrentValueCorrectValueInPolicy,
    inPolicyAndRoofYearUnchanged,
    policyBoundWithRoofExtendedSoWeHaveToHonorItIfRoofYearUnchanged,
    forcedScheduledRSCoverage,
    forcedExtendedRSCoverage,
    stateOverride,
    stateSpecificLockField
  );

  return {
    lockedRSField,
    forcedScheduledRSCoverage,
    forcedExtendedRSCoverage,
    isCurrentValueCorrectValueInPolicy,
    policyBoundWithRoofExtendedSoWeHaveToHonorItIfRoofYearUnchanged,
    // dont force state override if in policy and roof year hasnt changed
    stateOverride: stateOverride && !(fromPolicy && inPolicyAndRoofYearUnchanged)
  };
};
export default useExtendedRoofCoverage;
